import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../context/CartContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { cartCount } = useCart();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled || mobileMenuOpen ? 'bg-gallery/95 backdrop-blur-md py-4 shadow-sm' : 'bg-transparent py-7'
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 flex justify-between items-center">
        {/* Desktop Nav */}
        <nav className="hidden md:flex gap-10 text-[11px] font-semibold tracking-[0.2em] uppercase text-charcoal/90" aria-label="Main navigation">
          <Link to="/shop" className="hover:text-ultramarine transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine focus-visible:ring-offset-4 rounded-sm">The Collection</Link>
          <Link to="/info" className="hover:text-ultramarine transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine focus-visible:ring-offset-4 rounded-sm">Foundry Guide</Link>
        </nav>

        {/* Mobile Menu Toggle */}
        <button 
          className="md:hidden text-charcoal z-50 p-2 -ml-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-expanded={mobileMenuOpen}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          <SafeIcon icon={mobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} className="text-2xl" />
        </button>

        {/* Logo */}
        <Link 
          to="/" 
          className="text-2xl md:text-3xl font-serif font-semibold text-ultramarine tracking-tighter absolute left-1/2 -translate-x-1/2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine focus-visible:ring-offset-4 rounded-sm px-2"
          aria-label="FrameFoundry Home"
        >
          FrameFoundry
        </Link>

        {/* Utility */}
        <div className="flex items-center gap-2 md:gap-4">
          <button 
            className="hover:text-ultramarine transition-colors hidden sm:flex p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine rounded-full"
            aria-label="Search portfolio"
          >
            <SafeIcon icon={FiIcons.FiSearch} className="text-xl" />
          </button>
          <button 
            className="hover:text-ultramarine transition-colors relative p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine rounded-full"
            aria-label={`Shopping cart with ${cartCount} items`}
          >
            <SafeIcon icon={FiIcons.FiShoppingBag} className="text-xl" />
            <AnimatePresence>
              {cartCount > 0 && (
                <motion.span 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute top-0 right-0 bg-ultramarine text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold"
                >
                  {cartCount}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-full left-0 w-full bg-gallery border-t border-gray-100 py-12 px-8 flex flex-col items-center gap-8 md:hidden shadow-xl"
            role="dialog"
            aria-label="Mobile navigation"
          >
            <Link to="/shop" className="text-2xl font-serif p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine">The Collection</Link>
            <Link to="/info" className="text-2xl font-serif p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine">Foundry Guide</Link>
            <div className="pt-8 border-t border-gray-200 w-full flex justify-center gap-8">
              <a href="#" aria-label="Instagram" className="p-2 text-gray-500 hover:text-ultramarine focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine rounded-full">
                <SafeIcon icon={FiIcons.FiInstagram} className="text-xl" />
              </a>
              <a href="#" aria-label="Twitter" className="p-2 text-gray-500 hover:text-ultramarine focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine rounded-full">
                <SafeIcon icon={FiIcons.FiTwitter} className="text-xl" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;